/**
 * BlueBean「OPレポート」(agent_report_YYYY-MM-D.csv)の列検証と行の正規化。
 * 列名は 2026-09-08/09 の実CSVで確認したもの(Shift_JIS、33列、時間は "HH:MM:SS")。
 * 取得件数・架電会社はこのCSVには無い(対応履歴の取込で入れる)。
 */
import type { DateStr } from '@core/lib/types';
import type { ColumnMapping, CsvImporter, ImportFileInfo, ImportIssue, ImportResult, ParsedOpReportRow, RawRow } from './types';

/** 内部キー → 実CSVの列名 */
export const OP_REPORT_COLUMNS = {
  bb_id: 'ログインID',
  display_name: '名前',
  login_count: 'ログイン回数',
  login_sec: 'ログイン時間合計',
  reception_count: '受付回数',
  reception_sec: '受付時間合計',
  talk_count: '通話回数',
  talk_sec: '通話時間合計',
  after_count: '後処理回数',
  after_sec: '後処理時間合計',
  break_count: '休憩回数',
  break_sec: '休憩時間合計',
  away_count: '離席回数',
  away_sec: '離席時間合計',
  utilization_pct: '稼動率(%)',
} as const;

export const DEFAULT_OP_REPORT_MAPPING: ColumnMapping = {
  kind: 'op_report',
  columns: { ...OP_REPORT_COLUMNS },
  options: { timeUnit: 'hms', timezone: 'JST' },
};

/** "HH:MM:SS"(時は2桁以上も可) → 秒。空欄は null、形式が違えば NaN */
export function parseHms(s: string | undefined): number | null {
  const t = (s ?? '').trim();
  if (t === '') return null;
  const m = t.match(/^(\d+):(\d{1,2}):(\d{1,2})$/);
  if (!m) return NaN;
  return Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]);
}

function parseNum(s: string | undefined): number | null {
  const t = (s ?? '').trim().replace(/,/g, '');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
}

/** ファイル名 agent_report_2026-09-8.csv → 2026-09-08。読めなければ null */
export function dateFromFileName(name: string): DateStr | null {
  const m = name.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!m) return null;
  return `${m[1]}-${m[2]!.padStart(2, '0')}-${m[3]!.padStart(2, '0')}`;
}

export function normalizeBbId(s: string): string {
  return s.trim().toUpperCase();
}

export const opReportImporter: CsvImporter<ParsedOpReportRow> = {
  kind: 'op_report',

  validateHeader(headers, mapping) {
    const issues: ImportIssue[] = [];
    const have = new Set(headers.map((h) => h.trim()));
    const missing = Object.values(mapping.columns).filter((c) => !have.has(c));
    if (missing.length > 0) {
      issues.push({ level: 'error', code: 'header_mismatch', message: `OPレポートに必要な列がありません: ${missing.join(', ')}` });
    }
    return issues;
  },

  parse(rows, mapping, file) {
    const issues: ImportIssue[] = [];
    const byId = new Map<string, ParsedOpReportRow>();
    const col = (r: RawRow, key: keyof typeof OP_REPORT_COLUMNS) => r[mapping.columns[key] ?? ''];
    rows.forEach((r, i) => {
      const rowNo = i + 2; // ヘッダ=1
      const bbId = normalizeBbId(col(r, 'bb_id') ?? '');
      if (!bbId) {
        issues.push({ level: 'error', row: rowNo, code: 'empty_bb_id', message: 'ログインIDが空の行があります' });
        return;
      }
      const secs = {
        login_sec: parseHms(col(r, 'login_sec')),
        reception_sec: parseHms(col(r, 'reception_sec')),
        talk_sec: parseHms(col(r, 'talk_sec')),
        after_sec: parseHms(col(r, 'after_sec')),
        break_sec: parseHms(col(r, 'break_sec')),
        away_sec: parseHms(col(r, 'away_sec')),
      };
      const nums = {
        login_count: parseNum(col(r, 'login_count')),
        reception_count: parseNum(col(r, 'reception_count')),
        talk_count: parseNum(col(r, 'talk_count')),
        after_count: parseNum(col(r, 'after_count')),
        utilization_pct: parseNum(col(r, 'utilization_pct')),
      };
      const bad = [...Object.entries(secs), ...Object.entries(nums)].filter(([, v]) => typeof v === 'number' && Number.isNaN(v)).map(([k]) => k);
      if (bad.length > 0) {
        issues.push({ level: 'warning', row: rowNo, code: 'bad_number', message: `${bbId}: 数値・時間の形式が読めない列(${bad.join(', ')})があるため、この行を除外しました` });
        return;
      }
      const parsed: ParsedOpReportRow = { bb_id: bbId, display_name: (col(r, 'display_name') ?? '').trim(), ...nums, ...secs, source_row: rowNo };
      const prev = byId.get(bbId);
      if (prev) {
        // 同じBB IDが複数行(複数ログイン)は回数・時間を合計する(仕様 §12)
        issues.push({ level: 'warning', row: rowNo, code: 'duplicate_bb_id', message: `${bbId} が複数行あるため合計しました(${prev.source_row}行目と${rowNo}行目)` });
        const add = (a: number | null, b: number | null) => (a === null && b === null ? null : (a ?? 0) + (b ?? 0));
        byId.set(bbId, {
          ...prev,
          login_count: add(prev.login_count, parsed.login_count),
          login_sec: add(prev.login_sec, parsed.login_sec),
          reception_count: add(prev.reception_count, parsed.reception_count),
          reception_sec: add(prev.reception_sec, parsed.reception_sec),
          talk_count: add(prev.talk_count, parsed.talk_count),
          talk_sec: add(prev.talk_sec, parsed.talk_sec),
          after_count: add(prev.after_count, parsed.after_count),
          after_sec: add(prev.after_sec, parsed.after_sec),
          break_sec: add(prev.break_sec, parsed.break_sec),
          away_sec: add(prev.away_sec, parsed.away_sec),
          utilization_pct: null,
        });
      } else {
        byId.set(bbId, parsed);
      }
    });
    const info: ImportFileInfo = { ...file, kind: 'op_report' };
    const result: ImportResult<ParsedOpReportRow> = { rows: [...byId.values()], issues, file: info };
    return result;
  },
};
